<template>
  <FormGroup :error-container="errorContainer" :flex="flex" :name="name">
    <template v-slot="{id}">
      <label
        ref="checkBox"
        class="fm-checkbox"
        :class="{ 'fm-checkbox--checked': proxyValue, 'fm-checkbox--disabled': disabled }"
        tabindex="0"
        @keydown.space.prevent="toggleValue"
        @keydown.enter.prevent="toggleValue"
      >
        <input
          :id="id"
          ref="input"
          v-model="proxyValue"
          :disabled="disabled"
          class="fm-checkbox__input"
          type="checkbox"
          tabindex="-1"
        />

        <!-- Custom checkbox icon -->
        <span class="fm-checkbox__box" aria-hidden="true">
          <svg v-if="proxyValue" viewBox="0 0 12 12" fill="currentColor">
            <path d="M10.28 2.28a1 1 0 0 0-1.415 0L4.5 6.647 3.135 5.282a1 1 0 1 0-1.415 1.414l2.073 2.073a1 1 0 0 0 1.414 0l5.073-5.073a1 1 0 0 0 0-1.414z"/>
          </svg>
        </span>

        <span class="fm-checkbox__content">
          <slot v-bind="{ onClick: toggleValue, style: {} }">
            <span v-text="label"/>
          </slot>
        </span>
      </label>
    </template>
  </FormGroup>
</template>

<script>
import FormGroup from '@modules/shared/components/form/FormGroup';

export default {
  name: 'FmCheckbox',
  components: {FormGroup},
  props: {
    value: { required: false, default: false },
    errorContainer: { type: String, required: false, default: null },
    flex: { type: Boolean, required: false, default: false },
    name: { required: false, default: () => [] },
    label: { type: String, required: false, default: '' },
    disabled: { type: Boolean, required: false, default: false },
  },
  computed: {
    proxyValue: {
      set(value) { this.$emit('input', value); },
      get() { return this.value; },
    },
  },
  methods: {
    toggleValue() {
      if (!this.disabled) this.$emit('input', !this.value);
    },
  },
};
</script>

<style lang="scss" scoped>
$amber:       #FFB000;
$amber-light: rgba(255, 176, 0, 0.08);
$amber-ring:  rgba(255, 176, 0, 0.2);
$ink:         #15171C;
$ink-soft:    #5B606B;
$line:        #E7E4DA;
$surface:     #FAFAF8;

.fm-checkbox {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  background: $surface;
  border: 1.5px solid $line;
  border-radius: 12px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
  min-height: 48px;
  width: 100%;
  margin-bottom: 0.75rem;
  user-select: none;

  &:hover:not(.fm-checkbox--disabled) {
    border-color: $amber;
    background: $amber-light;
  }

  &:focus-within, &:focus {
    outline: none;
    border-color: $amber;
    box-shadow: 0 0 0 3px $amber-ring;
  }

  &--checked {
    border-color: $amber;
    background: $amber-light;
  }

  &--disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &__input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
    width: 0;
    height: 0;
  }

  &__box {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    border-radius: 6px;
    border: 2px solid $line;
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s, border-color 0.15s;
    margin-top: 1px; // optical alignment with first line of text

    svg {
      width: 12px;
      height: 12px;
      color: $ink;
    }
  }

  &--checked &__box {
    background: $amber;
    border-color: $amber;
  }

  &__content {
    font-size: 14px;
    line-height: 1.5;
    color: $ink-soft;
    flex: 1;
    min-width: 0;
  }

  &--checked &__content {
    color: $ink;
  }
}
</style>
